/**
 * Строка задания с полем ответа внутри (T4, 21.10.2026, мок на компьютере).
 *
 * Поле встаёт на место пропуска «____» в тексте вопроса, перед полем — квадратик с номером,
 * как на бланке IELTS. Структуры задания (форма, заметки, таблица)
 * сервер пока не отдаёт — формат `docs/tasks/T4-mock-task-layout.md`, делается потом с т.5;
 * сейчас раскладка выводится из вида задания и текста вопросов.
 */

export type Piece = { kind: 'text'; text: string } | { kind: 'gap' };

// Пропуск в тексте вопроса — три и больше подчёркиваний («____»). Многоточие пропуском не
// считается: «...» встречается и в обычном тексте, поле встало бы посреди фразы.
const BLANK = /_{3,}/g;

/**
 * Единственный пропуск в тексте вопроса или null. 🔴 СТРАХОВКА (слово владельца 21.10.2026):
 * пропуска нет или их больше одного — вопрос рисуется как раньше, с отдельным полем под ним.
 * Иначе поле встало бы не туда или не встало вовсе, а вопрос без поля — немая блокировка.
 */
function singleBlank(prompt: string): { at: number; len: number } | null {
  const all = [...prompt.matchAll(BLANK)];
  if (all.length !== 1) return null;
  return { at: all[0].index ?? 0, len: all[0][0].length };
}

/** Текст вопроса → [до][поле][после]; null — вопрос рисуется по-старому. */
export function inlinePrompt(prompt: string): Piece[] | null {
  const b = singleBlank(prompt);
  if (!b) return null;
  const out: Piece[] = [];
  const before = prompt.slice(0, b.at);
  const after = prompt.slice(b.at + b.len);
  if (before) out.push({ kind: 'text', text: before });
  out.push({ kind: 'gap' });
  if (after) out.push({ kind: 'text', text: after });
  return out;
}

/**
 * Форма и таблица — две колонки «фраза | поле». Если до пропуска есть двоеточие
 * («Name: Martyn ____»), слева — подпись до двоеточия, справа — остаток с полем внутри.
 * Иначе слева фраза (пропуск посередине — «…», в конце — убран), справа — поле.
 * null — пропуск не один: вопрос рисуется по-старому.
 */
export function twoColumns(prompt: string): { left: string; right: Piece[] } | null {
  const b = singleBlank(prompt);
  if (!b) return null;
  const colon = prompt.lastIndexOf(':', b.at);
  if (colon > 0) {
    return { left: prompt.slice(0, colon + 1).trim(), right: inlinePrompt(prompt.slice(colon + 1).trimStart()) ?? [] };
  }
  const before = prompt.slice(0, b.at).trimEnd();
  const after = prompt.slice(b.at + b.len).trim();
  // «… is spelled as ____.» — точка после пропуска не продолжение фразы, слева её не нужно.
  if (!after || /^[.,;!?]+$/.test(after)) return { left: before, right: [{ kind: 'gap' }] };
  return { left: `${before} … ${after}`, right: [{ kind: 'gap' }] };
}

/**
 * Подпись варианта выбора рядом с буквой в кружке. У старых вопросов пула буква уже вписана в
 * текст («A. Next to the big park»), и выходило «Ⓐ A. Next…». Метка срезается, только если её
 * буква совпадает с местом варианта (A у первого, B у второго); ответ — по-прежнему полный текст.
 */
export function choiceLabel(index: number, text: string): string {
  const m = /^([A-H])[.)]\s+/.exec(text);
  return m && m[1].charCodeAt(0) - 65 === index ? text.slice(m[0].length) : text;
}

/** Вид задания → раскладка карточки на компьютере. null — прежняя карточка без изменений. */
export type WideKind = 'lines' | 'notes' | 'columns' | 'choice';

export function wideKind(type: string): WideKind | null {
  switch (type) {
    case 'sentence_completion':
    case 'short_answer':
    case 'flowchart_completion':
    case 'diagram_label':
      return 'lines';
    case 'note_completion':
    case 'summary_completion':
      return 'notes';
    case 'table_completion':
    case 'form_completion':
      return 'columns';
    case 'mc_single':
    case 'mc_multi':
      // «Выберите ДВА» — те же буквы A–E в кружках, флажки вместо радио (T3, виды engnovate, 30.10.2026).
      return 'choice';
    default:
      // Сопоставление, True/False/Not Given — не в этот раз (слово владельца 21.10).
      return null;
  }
}
